import { Fragment, ReactNode, useEffect, useRef } from 'react';
import Head5 from '../Text/Head5';
import BodyLarge from '../Text/BodyLarge';
import Close from '../Close';
import ArrowBack from '../Icons/ArrowBack';
import { useSelector } from 'react-redux';
import handleEnableDisableScrollMobile from '../../../helpers/utils/handleEnableDisableScrollMobile';
import { IRootState } from '../../../store';

export interface ModalProps {
  onClose?: () => void;
  children: React.ReactNode;
  visible: boolean;
  modalClassName?: string;
  icon?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  headerClassName?: string;
  headerTitleClassName?: string;
  subTitleClassName?: string;
  title?: string;
  subTitle?: string | ReactNode;
  header?: boolean;
  btnCloseTestId?: string;
  titleTestId?: string;
  containerTestId?: string;
  overflow?: string;
  showBlur?: boolean;
  dataMC?: string;
  idItem?: string;
  hideCloseButton?: boolean;
  progressBarProgress?: number;
  isProgressBar?: boolean;
  showCloseButton?: boolean;
  bookingService?: boolean;
  sizeButtonClose?: number;
  isPreventScroll?: boolean;
  persistContent?: boolean;
  as?: string;
  isCloseClickOutside?: boolean;
  showBackButton?: boolean;
}

const Modal = ({
  onClose = () => {},
  children,
  visible = false,
  modalClassName = '',
  className = '',
  icon,
  bodyClassName = '',
  headerClassName = '',
  headerTitleClassName = '',
  subTitleClassName = '',
  title = '',
  subTitle = '',
  header = false,
  btnCloseTestId = null,
  titleTestId = null,
  containerTestId = 'dataTestWrapperModal',
  overflow = 'overflow-auto',
  showBlur = false,
  dataMC = '',
  idItem = '',
  hideCloseButton = false,
  isProgressBar = false,
  sizeButtonClose = 20,
  progressBarProgress = 0,
  showCloseButton = true,
  bookingService = false,
  isPreventScroll = false,
  persistContent = false,
  as,
  isCloseClickOutside = true,
  showBackButton = false,
  ...props
}: ModalProps & React.ComponentProps<'div'>) => {
  const { isMobileScreen } = useSelector((state: IRootState) => state.page);
  const refModal = useRef(null);
  const backdropModalRef = useRef(null);

  const closeOpenMenus = (e) => {
    if (
      visible &&
      !refModal.current?.contains(e.target) &&
      e.target === backdropModalRef.current &&
      isCloseClickOutside
    ) {
      onClose();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && visible) {
      onClose();
    }
  };

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
      if (isPreventScroll) {
        handleEnableDisableScrollMobile('enable', true);
      } else {
        handleEnableDisableScrollMobile('disable');
      }
    } else {
      document.body.style.overflow = 'unset';
      handleEnableDisableScrollMobile('enable');
    }
    return () => {
      document.body.style.overflow = 'unset';
      handleEnableDisableScrollMobile('enable');
    };
  }, [visible]);

  useEffect(() => {
    document.addEventListener('mousedown', closeOpenMenus);
    document.addEventListener('keydown', handleKeyDown);
    const pacContainer = document.querySelector('.pac-container');
    if (pacContainer) {
      pacContainer.addEventListener('mousedown', (e) => e.stopPropagation());
    }

    return () => {
      document.removeEventListener('mousedown', closeOpenMenus);
      document.removeEventListener('keydown', handleKeyDown);

      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer) {
        pacContainer.removeEventListener('mousedown', (e) => e.stopPropagation());
      }
    };
  });

  return (
    <Fragment>
      <div
        ref={backdropModalRef}
        data-testid={containerTestId}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        className={`${
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } duration-300 ease-in-out fixed w-[100vw] h-[100vh] ${
          isMobileScreen
            ? `inset-0 h-[100%] z-[55] `
            : `z-[70] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`
        } ${
          showBlur ? 'backdrop-blur-md bg-opacity-30 bg-black' : 'bg-opacity-30 bg-black'
        } ${modalClassName}`}
      >
        {(persistContent || visible) && (
          <div
            ref={refModal}
            data-testid="dataTestCardModal"
            className={`lg:max-h-[100vh] ${
              !visible && 'translate-y-full'
            } duration-300 ease-in-out flex flex-col absolute bg-white ${
              isMobileScreen
                ? `inset-1/2 transform -translate-x-1/2 -translate-y-1/2`
                : `rounded top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`
            } ${className}`}
            {...props}
          >
            {header && (
              <div
                className={`flex items-center ${showBackButton ? 'justify-start gap-4' : 'justify-between'} md-max-w:p-4 p-6 border-b border-reliableBlack20 align-middle ${headerClassName}`}
              >
                {showBackButton && (
                  <button
                    className="text-black mb-1"
                    aria-label="close-button"
                    onClick={onClose}
                    data-testid={btnCloseTestId}
                  >
                    <ArrowBack size={15} color="#000" />
                  </button>
                )}
                <div>
                  {!bookingService ? (
                    <Head5
                      className={`md-max-w:text-head5 ${headerTitleClassName}`}
                      data-testid={titleTestId}
                      as={as}
                    >
                      <div
                        id={idItem}
                        data-card={dataMC}
                        className="uppercase flex gap-5 items-center"
                      >
                        {icon}
                        <div className="line-clamp-2 flex-1 break-all pr-2">{title}</div>
                      </div>
                    </Head5>
                  ) : (
                    <p
                      className={`md-max-w:text-head5 ${headerTitleClassName}`}
                      data-testid={titleTestId}
                    >
                      <span id={idItem} data-card={dataMC} className="uppercase">
                        {icon}
                        {title}
                      </span>
                    </p>
                  )}

                  {subTitle && <BodyLarge className={subTitleClassName}>{subTitle}</BodyLarge>}
                </div>
                {!hideCloseButton && (
                  <div
                    onClick={onClose}
                    className={`flex justify-center items-center cursor-pointer w-8 h-8`}
                    data-testid={btnCloseTestId}
                  >
                    <Close size={sizeButtonClose} fill="#000" />
                  </div>
                )}
              </div>
            )}
            {isProgressBar && (
              <div className="w-full bg-gray-200 h-[3px] dark:bg-gray-700">
                <div
                  className="bg-[#4cc0ad] h-[3px]"
                  style={{ width: `${progressBarProgress}%` }}
                ></div>
              </div>
            )}
            <div className={`${!isMobileScreen && overflow} ${bodyClassName}`}>{children}</div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default Modal;
